/**
 * Hotel Service
 * AI-powered hotel recommendations using the hotel-ai Lambda (Bedrock Nova Lite)
 */

import { HOTEL_AI_API_BASE } from '../config/environment';
import { logger } from '../utils/logger';
import { authFetch } from '../utils/authFetch';

/**
 * Get hotel recommendations for a trip destination
 * @param {Object} params - Search parameters
 * @param {string} params.city - Destination city (required)
 * @param {string} params.checkIn - Check-in date (YYYY-MM-DD)
 * @param {string} params.checkOut - Check-out date (YYYY-MM-DD)
 * @param {number} params.guests - Number of guests (default: 1)
 * @param {string} params.budget - Budget tier: 'budget' | 'mid-range' | 'luxury' (default: 'mid-range')
 * @param {string} params.locationHint - Location preference (e.g., 'near the airport', 'downtown')
 * @param {string} params.brand - Preferred hotel brand (e.g., 'Marriott', 'no preference')
 * @returns {Promise<Array>} Array of hotel objects
 */
export async function getHotelRecommendations({
  city,
  checkIn,
  checkOut,
  guests = 1,
  budget = 'mid-range',
  locationHint = 'city center',
  brand = 'no preference'
} = {}) {
  if (!city) {
    throw new Error('City is required for hotel recommendations');
  }

  const queryParams = new URLSearchParams();
  queryParams.append('city', city);
  if (checkIn) queryParams.append('checkIn', checkIn);
  if (checkOut) queryParams.append('checkOut', checkOut);
  queryParams.append('guests', String(guests));
  queryParams.append('budget', budget);
  queryParams.append('locationHint', locationHint);
  queryParams.append('brand', brand);

  logger.debug('[HotelService] Fetching recommendations:', { city, checkIn, checkOut, budget, locationHint, brand });

  const response = await authFetch(`${HOTEL_AI_API_BASE}?${queryParams}`);

  if (!response.ok) {
    throw new Error(`Hotel AI API error: ${response.status}`);
  }

  const data = await response.json();

  if (!data.success || !data.hotels) {
    throw new Error(data.error || 'Invalid response from Hotel AI');
  }

  logger.debug(`[HotelService] Received ${data.hotels.length} recommendations for ${city}`);
  return data.hotels;
}

export default { getHotelRecommendations };
